import { CCol } from '@coreui/react';
import './style/style.scss';

export interface ITablePaginationProps {
    limit: number;
    totalPages: number;
    currentPage: number;
    onChangeLimit: (value: any) => void;
    onChangePage: (value: any) => void;

    hideLimit?: boolean;
    hidePages?: boolean;
    compact?: boolean;
}

const limitList = [10, 25, 50];

const buildCompactPages = (
    current: number,
    total: number,
    siblings: number,
    boundary: number,
) => {
    if (total <= 1) return [1];

    const pagesSet = new Set<number>();

    for (let p = 1; p <= Math.min(boundary, total); p += 1) pagesSet.add(p);

    for (let p = Math.max(total - boundary + 1, 1); p <= total; p += 1) pagesSet.add(p);

    const start = Math.max(current - siblings, 1);
    const end = Math.min(current + siblings, total);
    for (let p = start; p <= end; p += 1) pagesSet.add(p);

    const sorted = Array.from(pagesSet).sort((a, b) => a - b);

    const result: (number | '...')[] = [];
    for (let i = 0; i < sorted.length; i += 1) {
        const p = sorted[i];

        if (i === 0) {
            result.push(p);
            continue;
        }

        const prev = sorted[i - 1];
        const gap = p - prev - 1;

        if (gap === 0) {
            result.push(p);
        } else if (gap === 1) {
            result.push(prev + 1, p);
        } else if (gap === 2) {
            result.push(prev + 1, prev + 2, p);
        } else {
            result.push('...', p);
        }
    }

    return result;
};


const TablePagination = ({
    limit,
    totalPages,
    currentPage,
    onChangeLimit,
    onChangePage,
    hideLimit = false,
    hidePages = false,
    compact = false,
}: ITablePaginationProps) => {
    const safeTotal = Math.max(1, Number(totalPages) || 1);
    const safeCurrent = Math.min(Math.max(1, Number(currentPage) || 1), safeTotal);

    const pageList = buildCompactPages(
        safeCurrent,
        safeTotal,
        compact ? 0 : 1,
        compact ? 1 : 2,
    );

    const containerStyle = hidePages
        ? { display: 'flex', justifyContent: 'flex-end', width: '100%' }
        : undefined;

    return (
        <CCol className={`tablePagination ${hidePages ? 'limitOnly' : ''} ${compact ? 'compact' : ''}`} style={containerStyle}>
            {!hidePages && (
                <div className="pages">
                    <nav aria-label="Page navigation example">
                        <ul className="pagination" style={{ flexWrap: 'nowrap', overflow: 'hidden' }}>
                            <li className={safeCurrent === 1 ? 'page-item disabled' : 'page-item'}>
                                <button
                                    className="page-link"
                                    onClick={(e) => {
                                        (e.currentTarget as HTMLButtonElement).blur();
                                        if (safeCurrent !== 1) onChangePage(safeCurrent - 1);
                                    }}
                                    disabled={safeCurrent === 1}
                                    type="button"
                                >
                                    {compact ? 'Prev' : 'Previous'}
                                </button>
                            </li>

                            {pageList.map((value, idx) => {
                                if (value === '...') {
                                    return (
                                        <li className="page-item disabled" key={`ellipsis-${idx}`}>
                                            <button className="page-link" type="button">...</button>
                                        </li>
                                    );
                                }

                                return (
                                    <li
                                        className={value === safeCurrent ? 'page-item active' : 'page-item'}
                                        key={value}
                                        onClick={() => onChangePage(value)}
                                    >
                                        <button className="page-link" type="button">{value}</button>
                                    </li>
                                );
                            })}

                            <li className={safeCurrent === safeTotal ? 'page-item disabled' : 'page-item'}>
                                <button
                                    className="page-link"
                                    onClick={(e) => {
                                        (e.currentTarget as HTMLButtonElement).blur();
                                        if (safeCurrent < safeTotal) onChangePage(safeCurrent + 1);
                                    }}
                                    disabled={safeCurrent === safeTotal}
                                    type="button"
                                >
                                    Next
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            )}

            {!hideLimit && (
                <div className="limitRight">
                    <span className="limitText">Showing</span>

                    <select
                        className="limitSelect"
                        value={limit}
                        onChange={(e) => onChangeLimit(e.target.value)}
                    >
                        {limitList.map((v) => (
                            <option key={v} value={v}>{v}</option>
                        ))}
                    </select>

                    <span className="limitText">Records</span>
                </div>
            )}
        </CCol>
    );
};

export default TablePagination;
